import { Box, Link, Stack, Typography } from '@mui/material';
import { displaySx, numericSx } from '../../../theme/typography';
import { PanelCard } from './PanelCard';
import type { MyWeekDay, MyWeekWidget } from '../types';
import type { NavigateToTimesheetsOptions } from '../navigation';

interface MyWeekStripProps {
  data: MyWeekWidget | undefined;
  loading?: boolean;
  onNavigateToTimesheets?: (options?: NavigateToTimesheetsOptions) => void;
}

const STANDARD_DAY_HOURS = 8;
const DAY_LABELS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

/**
 * "My timesheet · this week" — exact prototype port of `MyWeekStrip`
 * (the v2 prototype, lines 276-328).
 *
 * 7-column day grid (Mon→Sun) with each cell carrying:
 *   - day label (MON 25)
 *   - 22-px hours value (`0` rendered as muted; non-zero coloured by band)
 *   - optional OT / +Xh callout / ON-CALL annotation
 *   - TODAY badge + accent-tint background on the current day
 *
 * Footer 4-column grid: Logged · Expected · Variance · Status.
 *
 * Hidden on `xs` and `sm` breakpoints (7-cell grid doesn't fit cleanly
 * below ~768px); a `MyWeekSummaryCard` fallback renders Logged /
 * Expected / Variance as a compact 3-stat block instead.
 */
export function MyWeekStrip({
  data,
  loading,
  onNavigateToTimesheets,
}: MyWeekStripProps) {
  const days = data?.days ?? [];
  const variance = (data?.hoursEntered ?? 0) - (data?.expectedHours ?? 0);
  const statusLabel = formatStatus(data?.status);
  const statusColour = data?.status === 'DRAFT' ? 'warning.main' : 'text.primary';

  const headerAction = onNavigateToTimesheets ? (
    <Link
      component="button"
      type="button"
      onClick={() =>
        onNavigateToTimesheets({
          tab: 'my-timesheet',
          weekISO: data?.weekStartISO,
        })
      }
      underline="hover"
      sx={{ fontSize: '0.8125rem' }}
    >
      Open timesheet →
    </Link>
  ) : undefined;

  if (loading && !data) {
    return (
      <PanelCard title="My timesheet · this week" action={headerAction} pad={18} data-testid="widget-my-week-strip">
        <Typography sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
          Loading…
        </Typography>
      </PanelCard>
    );
  }

  // No data at all → empty state (the prototype falls back here too).
  if (!data || days.length === 0) {
    return (
      <PanelCard title="My timesheet · this week" action={headerAction} pad={18} data-testid="widget-my-week-strip">
        <Typography sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
          No timesheet period yet for this week.
        </Typography>
      </PanelCard>
    );
  }

  return (
    <PanelCard
      title="My timesheet · this week"
      action={headerAction}
      pad={0}
      data-testid="widget-my-week-strip"
    >
      {/* Desktop 7-day strip — hidden on xs/sm */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Box
          sx={(theme) => ({
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            borderBottom: `1px solid ${theme.palette.divider}`,
          })}
        >
          {days.slice(0, 7).map((day, i) => (
            <DayCell
              key={day.dateISO ?? i}
              day={day}
              dayLabel={DAY_LABELS[i] ?? ''}
              isLast={i === 6}
            />
          ))}
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            px: '18px',
            py: '12px',
            gap: 1,
            fontSize: '0.75rem',
          }}
        >
          <FooterStat label="Logged" value={`${formatHours(data.hoursEntered)}h`} />
          <FooterStat label="Expected" value={`${formatHours(data.expectedHours)}h`} />
          <FooterStat
            label="Variance"
            value={`${variance >= 0 ? '+' : '−'}${formatHours(Math.abs(variance))}h`}
            valueColor={variance < 0 ? 'error.main' : 'success.main'}
          />
          <FooterStat
            label="Status"
            value={statusLabel}
            valueColor={statusColour}
            align="right"
          />
        </Box>
      </Box>

      {/* Mobile fallback — compact summary card with just the 3 totals */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, px: '18px', py: '16px' }}>
        <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
          <FooterStat label="Logged" value={`${formatHours(data.hoursEntered)}h`} />
          <FooterStat label="Expected" value={`${formatHours(data.expectedHours)}h`} />
          <FooterStat
            label="Variance"
            value={`${variance >= 0 ? '+' : '−'}${formatHours(Math.abs(variance))}h`}
            valueColor={variance < 0 ? 'error.main' : 'success.main'}
          />
          <FooterStat
            label="Status"
            value={statusLabel}
            valueColor={statusColour}
          />
        </Stack>
      </Box>
    </PanelCard>
  );
}

// ─── day cell ──────────────────────────────────────────────────────────────

interface DayCellProps {
  day: MyWeekDay;
  dayLabel: string;
  isLast: boolean;
}

function DayCell({ day, dayLabel, isLast }: DayCellProps) {
  const isToday = isTodayISO(day.dateISO);
  const dateNumber = parseDayOfMonth(day.dateISO);
  const totalHours = day.hours + day.onCallHours;
  const hasHours = totalHours > 0;
  const isNonWorking = !!day.isNonWorking;

  // Status drives both cell background tint and value text colour.
  //   'mute'   — non-working day, nothing logged (weekend / leave / bank holiday)
  //   'ot'     — non-working day with hours logged (or working day > 8h)
  //   'under'  — working day, hours < standard (incl. nothing logged)
  //   'ok'     — working day, hours >= standard
  let status: 'mute' | 'ot' | 'under' | 'ok';
  if (isNonWorking && !hasHours) status = 'mute';
  else if (day.isOvertime) status = 'ot';
  else if (day.hours < STANDARD_DAY_HOURS) status = 'under';
  else status = 'ok';

  return (
    <Box
      sx={(theme) => {
        const bgByStatus = {
          mute: 'transparent',
          under: theme.palette.statusSoft.warn,
          ok: theme.palette.statusSoft.ok,
          // OT uses the danger soft tint so non-working-day work stands
          // out from a normal under/ok working day.
          ot: theme.palette.statusSoft.danger,
        };
        const bg = isToday ? theme.palette.accent.tint : bgByStatus[status];
        return {
          position: 'relative',
          textAlign: 'center',
          py: '14px',
          px: '10px',
          backgroundColor: bg,
          borderRight: isLast ? 'none' : `1px solid ${theme.palette.divider}`,
        };
      }}
    >
      {isToday ? (
        <Box
          component="span"
          sx={{
            position: 'absolute',
            top: 6,
            right: 8,
            fontSize: '0.5625rem',
            color: 'primary.main',
            fontWeight: 700,
            letterSpacing: '0.06em',
          }}
        >
          TODAY
        </Box>
      ) : null}
      <Typography
        component="div"
        sx={{
          fontSize: '0.625rem',
          color: 'text.disabled',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          lineHeight: 1,
        }}
      >
        {dayLabel}
      </Typography>
      <Typography
        component="div"
        sx={[
          numericSx,
          {
            fontSize: '0.6875rem',
            color: 'text.disabled',
            lineHeight: 1,
            mt: '4px',
          },
        ]}
      >
        {dateNumber}
      </Typography>
      <Typography
        component="div"
        sx={[
          numericSx,
          displaySx,
          {
            fontSize: '1.375rem', // 22px
            fontWeight: 500,
            lineHeight: 1.05,
            mt: '6px',
            color:
              status === 'mute'
                ? 'text.disabled'
                : status === 'ot'
                  ? 'error.main'
                  : status === 'under'
                    ? 'warning.main'
                    : 'success.main',
          },
        ]}
      >
        {formatHours(day.hours)}
        <Box component="span" sx={{ fontSize: '0.6875rem', ml: '1px' }}>
          h
        </Box>
      </Typography>
      {day.isOvertime ? (
        <Box
          component="div"
          sx={{
            fontSize: '0.5625rem',
            color: 'error.main',
            mt: '2px',
            fontWeight: 600,
            letterSpacing: '0.06em',
          }}
        >
          OT
        </Box>
      ) : null}
      {day.onCallHours > 0 ? (
        <Box
          component="div"
          sx={{
            fontSize: '0.5625rem',
            color: 'text.disabled',
            mt: '2px',
            fontWeight: 500,
            letterSpacing: '0.04em',
          }}
        >
          ON-CALL · {formatHours(day.onCallHours)}h
        </Box>
      ) : null}
    </Box>
  );
}

// ─── helpers ───────────────────────────────────────────────────────────────

interface FooterStatProps {
  label: string;
  value: string;
  valueColor?: string;
  align?: 'left' | 'right';
}

function FooterStat({ label, value, valueColor = 'text.primary', align = 'left' }: FooterStatProps) {
  return (
    <Box sx={{ textAlign: align }}>
      <Box component="span" sx={{ color: 'text.disabled', fontSize: '0.75rem', mr: '6px' }}>
        {label}
      </Box>
      <Box
        component="span"
        sx={[
          numericSx,
          {
            color: valueColor,
            fontWeight: 600,
            fontSize: '0.8125rem',
          },
        ]}
      >
        {value}
      </Box>
    </Box>
  );
}

function formatHours(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(1);
}

function formatStatus(status: MyWeekWidget['status'] | undefined): string {
  switch (status) {
    case 'NOT_STARTED':
      return 'Not started';
    case 'DRAFT':
      return 'Draft';
    case 'SUBMITTED':
      return 'Submitted';
    case 'APPROVED':
      return 'Approved';
    case 'REJECTED':
      return 'Rejected';
    default:
      return '—';
  }
}

function isTodayISO(iso: string): boolean {
  if (!iso) return false;
  const today = new Date();
  const todayISO = `${today.getFullYear()}-${pad2(today.getMonth() + 1)}-${pad2(today.getDate())}`;
  return iso === todayISO;
}

function parseDayOfMonth(iso: string): string {
  if (!iso) return '';
  // YYYY-MM-DD; we want DD without leading zero stripped for display.
  const parts = iso.split('-');
  if (parts.length !== 3) return '';
  return String(Number(parts[2]));
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}
